<script setup>
import { computed, ref } from 'vue';
import axios from 'axios';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import BaseBubble from './Base.vue';
import SzAudioChip from 'dashboard/components/widgets/conversation/solarz/SzAudioChip.vue';
import MessageStatus from '../MessageStatus.vue';
import { useMessageContext } from '../provider.js';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import { MESSAGE_STATUS, MESSAGE_TYPES } from '../constants';

const { attachments, createdAt, status, messageType, isPrivate, sourceId, content } = useMessageContext();

// For group incoming messages the connector prefixes `*pushName*:\n` on the
// content — render it above the audio chip so agents still see who in the
// group sent the audio (e.g. "Fernando:").
const captionHtml = computed(() => {
  const raw = (content.value || '').trim();
  if (!raw) return '';
  // Only show captions that are clearly a sender label — don't render arbitrary user text
  // that could conflict with the audio experience. Match `*name*:` or `name:` on a single line.
  const m = raw.match(/^\*([^*\n]+)\*\s*:\s*$/m) || raw.match(/^([^:\n]{1,60}):\s*$/m);
  if (!m) return '';
  const escaped = m[1].replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  return `<em>${escaped}</em>:`;
});

const store = useStore();
const attachment = computed(() => attachments.value[0]);
const audioDuration = ref('0:00');

function onDurationUpdate(val) {
  audioDuration.value = val;
}

const readableTime = computed(() => messageTimestamp(createdAt.value, 'LLL d, h:mm a'));

// ── Transcribe (moved from SzAudioChip for Figma layout) ──
const szTranscribing = ref(false);
const szLocalTranscript = ref('');
const szTranscriptVisible = ref(true);
// SZ: insere quebras de linha em finais de frase (. ? !) seguidos de letra maiuscula/acentuada,
// e em pontos com pausa (vírgula seguida de muita palavra). Replica estilo WhatsApp.
function szBreakIntoParagraphs(raw) {
  if (!raw) return '';
  let t = String(raw).trim();
  // Normaliza espacos
  t = t.replace(/\s+/g, ' ');
  // 1) Quebra apos . ? ! seguido de espaco + letra maiuscula ou acentuada maiuscula
  t = t.replace(/([.?!])\s+(?=[A-ZÁÉÍÓÚÂÊÔÃÕÀÇ])/g, '$1\n\n');
  // 2) Quebra apos . ? ! quando ja eh fim da frase (qualquer letra a seguir)
  t = t.replace(/([.?!])\s+(?=[a-záéíóúâêôãõàç])/g, '$1\n\n');
  return t;
}

const szDisplayedTranscript = computed(() => {
  const raw = attachment.value?.transcribedText
    || attachment.value?.transcribed_text
    || szLocalTranscript.value
    || '';
  return szBreakIntoParagraphs(raw);
});
const szShowTranscribeBtn = computed(
  () => !szDisplayedTranscript.value && !szTranscribing.value
);

async function szTranscribe() {
  if (szTranscribing.value || !attachment.value) return;
  const cwToken = store.getters.getCurrentUser?.access_token;
  if (!cwToken) {
    useAlert('Sessão expirada, faça login novamente');
    return;
  }
  szTranscribing.value = true;
  try {
    const { data } = await axios.post(
      `/ai-chat/api/attachments/${attachment.value.id}/transcribe`,
      { audio_url: attachment.value.dataUrl },
      { headers: { 'X-CW-Token': cwToken }, timeout: 120000 }
    );
    szLocalTranscript.value = data?.transcribed_text || '';
    if (!szLocalTranscript.value) useAlert('Transcrição vazia');
  } catch (e) {
    const msg = e?.response?.data?.error || e?.message || 'Falha ao transcrever áudio';
    useAlert(msg);
  } finally {
    szTranscribing.value = false;
  }
}

function szToggleTranscript() {
  szTranscriptVisible.value = !szTranscriptVisible.value;
}

const showStatusIndicator = computed(() => {
  if (isPrivate.value) return false;
  if (messageType.value !== MESSAGE_TYPES.OUTGOING) return false;
  return true;
});

const statusToShow = computed(() => {
  if (sourceId.value) return status.value;
  return status.value === MESSAGE_STATUS.SENT ? MESSAGE_STATUS.DELIVERED : status.value;
});
</script>

<template>
  <BaseBubble class="bg-transparent" data-bubble-name="audio" hide-meta>
    <div
      v-if="captionHtml"
      class="sz-audio-caption"
      v-html="captionHtml"
    />
    <SzAudioChip
      :attachment="attachment"
      class="p-2 text-n-slate-12 skip-context-menu"
      @duration-update="onDurationUpdate"
    />
    <!-- Row: Transcrever pill (left) + date + mic + check (right) -->
    <div class="sz-audio-meta-row">
      <button
        v-if="!szDisplayedTranscript && !szTranscribing"
        class="sz-audio-transcribe-btn"
        type="button"
        @click.stop="szTranscribe"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/>
          <path d="m15 5 4 4"/>
        </svg>
        <span>Transcrever</span>
      </button>
      <div
        v-else-if="szTranscribing"
        class="sz-audio-transcribe-btn sz-audio-transcribe-btn--loading"
        aria-live="polite"
      >
        <span class="sz-audio-spinner" />
        <span>Transcrevendo...</span>
      </div>
      <button
        v-else
        class="sz-audio-transcribe-btn sz-audio-transcribe-btn--toggle"
        type="button"
        :aria-expanded="szTranscriptVisible"
        @click.stop="szToggleTranscript"
      >
        <svg
          class="sz-audio-transcribe-chevron"
          :class="{ 'sz-audio-transcribe-chevron--up': szTranscriptVisible }"
          width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"
        >
          <polyline points="6 9 12 15 18 9"/>
        </svg>
        <span>Transcrição</span>
      </button>
      <span class="sz-audio-meta-spacer" />
      <svg class="sz-audio-mic-icon" width="10" height="14" viewBox="0 0 13 19" fill="none">
        <path d="M2.5 8.75V3.75C2.5 2.75544 2.89509 1.80161 3.59835 1.09835C4.30161 0.395088 5.25544 0 6.25 0C7.24456 0 8.19839 0.395088 8.90165 1.09835C9.60491 1.80161 10 2.75544 10 3.75V8.75C10 9.74456 9.60491 10.6984 8.90165 11.4017C8.19839 12.1049 7.24456 12.5 6.25 12.5C5.25544 12.5 4.30161 12.1049 3.59835 11.4017C2.89509 10.6984 2.5 9.74456 2.5 8.75ZM12.5 8.75C12.5 8.58424 12.4342 8.42527 12.3169 8.30806C12.1997 8.19085 12.0408 8.125 11.875 8.125C11.7092 8.125 11.5503 8.19085 11.4331 8.30806C11.3158 8.42527 11.25 8.58424 11.25 8.75C11.25 10.0761 10.7232 11.3479 9.78553 12.2855C8.84785 13.2232 7.57608 13.75 6.25 13.75C4.92392 13.75 3.65215 13.2232 2.71447 12.2855C1.77678 11.3479 1.25 10.0761 1.25 8.75C1.25 8.58424 1.18415 8.42527 1.06694 8.30806C0.949731 8.19085 0.79076 8.125 0.625 8.125C0.45924 8.125 0.300269 8.19085 0.183058 8.30806C0.0658481 8.42527 0 8.58424 0 8.75C0.00190321 10.2989 0.578064 11.792 1.61707 12.9407C2.65607 14.0893 4.0841 14.812 5.625 14.9688V17.5C5.625 17.6658 5.69085 17.8247 5.80806 17.9419C5.92527 18.0592 6.08424 18.125 6.25 18.125C6.41576 18.125 6.57473 18.0592 6.69194 17.9419C6.80915 17.8247 6.875 17.6658 6.875 17.5V14.9688C8.4159 14.812 9.84393 14.0893 10.8829 12.9407C11.9219 11.792 12.4981 10.2989 12.5 8.75Z" fill="#A1A1A1"/>
      </svg>
      <time>{{ readableTime }}</time>
      <MessageStatus v-if="showStatusIndicator" :status="statusToShow" />
    </div>
    <div v-if="szDisplayedTranscript && szTranscriptVisible" class="sz-audio-transcript">
      &ldquo;{{ szDisplayedTranscript }}&rdquo;
    </div>
  </BaseBubble>
</template>

<style>
.sz-audio-caption {
  font-size: 13px;
  line-height: 1.4;
  padding: 4px 10px 0;
  color: inherit;
  opacity: .85;
}
.sz-audio-caption em {
  font-style: italic;
  font-weight: 500;
}

.sz-audio-meta-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
  padding: 0 4px;
  line-height: 20px;
  font-size: 12px;
  font-family: Inter, system-ui, sans-serif;
}
.sz-audio-meta-row time {
  color: #000000;
  font-weight: 400;
}
.dark .sz-audio-meta-row time {
  color: #D4D4D4 !important;
}
.dark .sz-audio-meta-row .sz-audio-mic-icon path {
  fill: #8ACE8A !important;
}
.sz-audio-meta-spacer {
  flex: 1;
}
.sz-audio-mic-icon {
  flex-shrink: 0;
}

/* Transcribe pill (moved from SzAudioChip) */
.sz-audio-transcribe-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border: none;
  border-radius: 999px;
  background: rgba(35,109,35,0.08);
  color: #236D23;
  font-size: 12px;
  font-weight: 600;
  font-family: Inter, system-ui, sans-serif;
  cursor: pointer;
  line-height: 16px;
  transition: background 0.15s, opacity 0.15s;
}
.sz-audio-transcribe-btn:hover { background: rgba(35,109,35,0.16); }
.sz-audio-transcribe-btn--loading { cursor: progress; opacity: 0.85; }
.sz-audio-spinner {
  width: 10px;
  height: 10px;
  border: 2px solid rgba(35,109,35,0.25);
  border-top-color: #236D23;
  border-radius: 50%;
  animation: sz-audio-spin 0.8s linear infinite;
  flex-shrink: 0;
}
@keyframes sz-audio-spin { to { transform: rotate(360deg); } }

.sz-audio-transcript {
  margin: 4px 4px 0;
  font-size: 13px;
  font-style: italic;
  color: inherit;
  padding: 8px 10px;
  background: rgba(0,0,0,0.04);
  border-radius: 8px;
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 1.35;
  /* SZ: preserva \n vindos do LLM line-break + limita largura pra nao estourar a bolha */
  white-space: pre-wrap;
  max-width: 320px;
  width: 100%;
  box-sizing: border-box;
}
.dark .sz-audio-transcript { background: rgba(255,255,255,0.06); }
.dark .sz-audio-transcribe-btn { color: #8ACE8A; background: rgba(138,206,138,0.12); }
.dark .sz-audio-transcribe-btn:hover { background: rgba(138,206,138,0.22); }
.dark .sz-audio-spinner { border-color: rgba(138,206,138,0.25); border-top-color: #8ACE8A; }

.sz-audio-transcribe-chevron {
  transition: transform 0.2s ease;
}
.sz-audio-transcribe-chevron--up {
  transform: rotate(180deg);
}
</style>
